import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const TYPING_SPEED = 18;
const LINE_PAUSE   = 130;

/* ─────────── STATIONS ─────────── */
const stations = [
  {
    id: 'boot', stationName: 'START', chapter: null, title: null, year: '', icon: '⚡', isLast: false,
    lines: [
      '> JOURNEY INITIALIZED...',
      '> Mapping life events ............. OK',
      '> Loading character data .......... OK',
      '> Boarding immersive experience ... OK',
      '',
      '  Welcome aboard.',
      '  This is the story of Ashwin Sridharan.',
      '',
      '  Destination:  The Future.',
      '  Stops ahead:  7.',
    ],
    prompt: '[ BOARD THE TRAIN ]',
  },
  {
    id: 'origins', stationName: 'CHENNAI', chapter: 'Chapter I', title: 'Origins', year: 'Born', icon: '🌆', isLast: false,
    lines: [
      'A builder is born in Chennai, India.',
      '',
      'From the very beginning — a spark.',
      'A relentless curiosity about how things work.',
      '',
      'How do machines think?',
      'How do systems breathe?',
      '',
      'The journey from infrastructure',
      'to intelligence begins.',
    ],
    prompt: '[ NEXT STOP → ]',
  },
  {
    id: 'anna-university', stationName: 'ANNA UNIV', chapter: 'Chapter II', title: 'The First Quest', year: '2019', icon: '🎓', isLast: false,
    lines: [
      'September 2019. The doors open.',
      '',
      'Anna University, Chennai.',
      'B.Tech — Information Technology.',
      '',
      'Operating Systems. Computer Networks.',
      'Data Structures. Cryptography. Cloud Computing.',
      '',
      '> GPA: 9.28 / 10',
      '> Department Topper.',
      '> University 9th Rank.',
      '',
      'The foundations are forged.',
    ],
    prompt: '[ NEXT STOP → ]',
  },
  {
    id: 'iit-madras', stationName: 'IIT MADRAS', chapter: 'Chapter III', title: 'A New Realm', year: '2022', icon: '🧠', isLast: false,
    lines: [
      'February 2022. A second path opens.',
      '',
      'Indian Institute of Technology Madras.',
      'B.Sc. in Data Science & Applications.',
      '',
      'Machine Learning. Deep Learning. MLOps.',
      'Teaching machines to understand data.',
      '',
      '> GPA: 8.01 / 10',
      '',
      'Machine learning becomes a second language.',
      '',
      'Two degrees. Twice the power.',
    ],
    prompt: '[ NEXT STOP → ]',
  },
  {
    id: 'virtusa', stationName: 'VIRTUSA', chapter: 'Chapter IV', title: 'First Battle', year: 'Jan 2023', icon: '⚔', isLast: false,
    lines: [
      'January 2023.',
      '',
      'First contact with the industry.',
      'Virtusa — Software Engineering Intern.',
      '',
      '> Spring Boot & Java ......... equipped.',
      '> Docker and JDBC ............ mastered.',
      '',
      'The intern has survived.',
      'Experience gained. Skills leveled up.',
    ],
    prompt: '[ NEXT STOP → ]',
  },
  {
    id: 'jci', stationName: 'JOHNSON', chapter: 'Chapter V', title: 'The Main Quest', year: 'May 2023', icon: '🚀', isLast: false,
    lines: [
      'May 2023. The platform chapter begins.',
      '',
      'Johnson Controls',
      'Graduate Engineer → Developer 3.',
      '',
      '> Kubernetes: AWS · Azure · GCP · on-prem.',
      '> 99.9%+ availability across business-critical workloads.',
      '> 50+ engineers on a self-service delivery platform.',
      '> Release cycles reduced to under 15 minutes.',
      '> MTTR reduced by 40% across 20+ P0/P1 incidents.',
      '> Cloud costs reduced by 25–30%.',
      '',
      'Systems: resilient. Engineer: just getting started.',
    ],
    prompt: '[ NEXT STOP → ]',
  },
  {
    id: 'skills', stationName: 'ARSENAL', chapter: 'Chapter VI', title: 'Skill Tree', year: 'Ongoing', icon: '🛡', isLast: false,
    lines: [
      '>> SKILL TREE UNLOCKED <<',
      '',
      '[ PLATFORM ENGINEERING ]',
      '  Kubernetes · CRDs · Operators · Terraform · Helm',
      '',
      '[ AI INFRASTRUCTURE ]',
      '  Kueue · vLLM · GPU Scheduling · MIG · NCCL',
      '',
      '[ AI & DISTRIBUTED SYSTEMS ]',
      '  LangGraph · Azure OpenAI · RAG · KV Cache · RDMA',
      '',
      '[ RELIABILITY ]',
      '  Prometheus · Grafana · Loki · SLOs · Error Budgets',
      '',
      '[ LANGUAGES ]',
      '  Go · Python · Bash · SQL · Java',
      '',
      'The platform is ready for its next workload.',
    ],
    prompt: '[ FINAL STOP → ]',
  },
  {
    id: 'finale', stationName: 'FUTURE', chapter: 'Chapter VII', title: 'Journey Continues', year: 'Present', icon: '✨', isLast: true,
    lines: [
      'The story is not over.',
      '',
      'New quests await on the horizon.',
      'GPU orchestration. LLM inference. Distributed intelligence.',
      '',
      'Every system built — a legacy.',
      'Every problem solved — a dragon slain.',
      '',
      '────────────────────────────────',
      '',
      '> Name  :  Ashwin Sridharan',
      '> Role  :  Platform Engineer',
      '> Class :  AI Infrastructure · Reliability · Systems',
      '',
      '────────────────────────────────',
    ],
    prompt: '[ ENTER PORTFOLIO ]',
  },
];

/* ─── line colour ─── */
function lineColor(line) {
  if (line.startsWith('>>'))                      return '#2dd4bf';
  if (line.startsWith('> '))                      return '#4ade80';
  if (line.startsWith('[') && line.endsWith(']')) return '#fbbf24';
  if (line.startsWith('─'))                       return '#1e293b';
  return '#cbd5e1';
}

/* ─── Journey Track ─── */
function JourneyTrack({ currentIndex }) {
  const N   = stations.length;
  const pct = currentIndex / (N - 1);

  return (
    <div style={{ position: 'relative', width: '100%', padding: '0 24px', boxSizing: 'border-box' }}>
      {/* rail */}
      <div style={{ position: 'relative', height: 4, borderRadius: 2, background: '#0f172a', margin: '8px 0' }}>
        {/* completed */}
        <motion.div
          style={{ position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 2, background: 'linear-gradient(90deg,#0f766e,#2dd4bf)' }}
          animate={{ width: `${pct * 100}%` }}
          transition={{ duration: 0.75, ease: 'easeInOut' }}
        />
        {/* station dots */}
        {stations.map((s, i) => {
          const done   = i < currentIndex;
          const active = i === currentIndex;
          const posPct = i / (N - 1);
          return (
            <div
              key={s.id}
              style={{
                position: 'absolute',
                left: `${posPct * 100}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 2,
              }}
            >
              <motion.div
                animate={{ scale: active ? 1.25 : 1 }}
                transition={{ duration: 0.3 }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: `2px solid ${active ? '#2dd4bf' : done ? '#0d9488' : '#334155'}`,
                  background: done ? '#0d9488' : '#050a12',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  boxShadow: active ? '0 0 20px rgba(45,212,191,0.65), 0 0 0 4px rgba(45,212,191,0.12)' : done ? '0 0 8px rgba(45,212,191,0.35)' : 'none',
                  opacity: !done && !active ? 0.4 : 1,
                }}
              >
                {s.icon}
              </motion.div>
              {/* label */}
              <span style={{
                marginTop: 6,
                fontFamily: 'monospace',
                fontSize: 9,
                letterSpacing: '0.1em',
                color: done || active ? '#2dd4bf' : '#334155',
                whiteSpace: 'nowrap',
              }}>
                {s.stationName}
              </span>
              <span style={{
                fontFamily: 'monospace',
                fontSize: 8,
                color: done || active ? '#475569' : '#1e293b',
              }}>
                {s.year}
              </span>
            </div>
          );
        })}
        {/* animated train dot */}
        <motion.div
          style={{ position: 'absolute', top: '50%', zIndex: 3 }}
          animate={{ left: `${pct * 100}%` }}
          transition={{ duration: 0.75, ease: 'easeInOut' }}
        >
          <div style={{ position: 'relative', transform: 'translate(-50%, -50%)' }}>
            <motion.div
              style={{ position: 'absolute', top: -10, left: -10, width: 20, height: 20, borderRadius: '50%', background: 'rgba(45,212,191,0.2)' }}
              animate={{ scale: [1, 2.5, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 1.6, repeat: Infinity }}
            />
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#2dd4bf', boxShadow: '0 0 14px rgba(45,212,191,1)', marginTop: -7, marginLeft: -7 }} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export default function StoryMode({ onComplete }) {
  const [sceneIndex,     setSceneIndex]     = useState(0);
  const [displayedLines, setDisplayedLines] = useState([]);
  const [currentLine,    setCurrentLine]    = useState('');
  const [lineIndex,      setLineIndex]      = useState(0);
  const [charIndex,      setCharIndex]      = useState(0);
  const [isTypingDone,   setIsTypingDone]   = useState(false);
  const [isExiting,      setIsExiting]      = useState(false);
  const [isTraveling,    setIsTraveling]    = useState(false);
  const timerRef = useRef(null);
  const scene = stations[sceneIndex];

  const exitStory = useCallback(() => {
    clearTimeout(timerRef.current);
    setIsExiting(true);
    setTimeout(onComplete, 600);
  }, [onComplete]);

  const skipTyping = useCallback(() => {
    clearTimeout(timerRef.current);
    setDisplayedLines(scene.lines);
    setCurrentLine('');
    setLineIndex(scene.lines.length);
    setIsTypingDone(true);
  }, [scene]);

  const advance = useCallback(() => {
    if (!isTypingDone) { skipTyping(); return; }
    if (scene.isLast)  { exitStory();  return; }
    setIsTraveling(true);
    setTimeout(() => { setSceneIndex(i => i + 1); setIsTraveling(false); }, 650);
  }, [isTypingDone, scene, exitStory, skipTyping]);

  useEffect(() => {
    setDisplayedLines([]);
    setCurrentLine('');
    setLineIndex(0);
    setCharIndex(0);
    setIsTypingDone(false);
  }, [sceneIndex]);

  useEffect(() => {
    if (isTypingDone || isTraveling) return;
    clearTimeout(timerRef.current);
    const lines = scene.lines;
    if (lineIndex >= lines.length) { setIsTypingDone(true); return; }
    const full = lines[lineIndex];
    if (charIndex < full.length) {
      timerRef.current = setTimeout(() => {
        setCurrentLine(p => p + full[charIndex]);
        setCharIndex(c => c + 1);
      }, TYPING_SPEED);
    } else {
      timerRef.current = setTimeout(() => {
        setDisplayedLines(p => [...p, full]);
        setCurrentLine('');
        setCharIndex(0);
        setLineIndex(l => l + 1);
      }, LINE_PAUSE);
    }
    return () => clearTimeout(timerRef.current);
  }, [lineIndex, charIndex, isTypingDone, isTraveling, scene]);

  useEffect(() => {
    const h = e => {
      if (['Enter', ' ', 'ArrowRight'].includes(e.key)) { e.preventDefault(); advance(); }
      else if (e.key === 'Escape') exitStory();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [advance, exitStory]);

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          onClick={advance}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            background: '#050a12',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          {/* ambient glows */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: '-20%', left: '20%', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle,rgba(20,184,166,0.12) 0%,transparent 65%)', filter: 'blur(40px)' }} />
            <div style={{ position: 'absolute', bottom: '10%', right: '15%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle,rgba(109,40,217,0.08) 0%,transparent 65%)', filter: 'blur(40px)' }} />
          </div>
          {/* dot grid */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(circle,rgba(148,163,184,0.055) 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
          {/* scanlines */}
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 3px,rgba(0,0,0,0.05) 3px,rgba(0,0,0,0.05) 4px)' }} />

          {/* ── TOP BAR ── */}
          <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 10px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.3em', color: scene.chapter ? 'rgba(45,212,191,0.7)' : 'rgba(100,116,139,0.5)' }}>
                {scene.chapter || 'INTRO'}
              </span>
              {scene.title && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(100,116,139,0.5)' }}>
                  · {scene.title}
                </span>
              )}
            </div>
            <button
              onClick={e => { e.stopPropagation(); exitStory(); }}
              style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(100,116,139,0.6)', border: '1px solid rgba(30,41,59,0.8)', background: 'rgba(5,10,18,0.8)', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', letterSpacing: '0.1em' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#2dd4bf'; e.currentTarget.style.borderColor = 'rgba(45,212,191,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(100,116,139,0.6)'; e.currentTarget.style.borderColor = 'rgba(30,41,59,0.8)'; }}
            >
              SKIP ›
            </button>
          </div>

          {/* ── CENTRE CONTENT ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 20px', position: 'relative', zIndex: 10, minHeight: 0, overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={sceneIndex}
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
                style={{ width: '100%', maxWidth: 680 }}
              >
                {/* ── CHAPTER HEADER ── big + bold ── */}
                {scene.chapter && (
                  <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.45em', textTransform: 'uppercase', color: 'rgba(45,212,191,0.6)', marginBottom: 8 }}>
                      {scene.chapter}
                    </p>
                    <h2 style={{
                      fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                      fontWeight: 800,
                      lineHeight: 1.1,
                      letterSpacing: '-0.02em',
                      background: 'linear-gradient(135deg,#f8fafc 0%,#2dd4bf 55%,#0891b2 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      margin: '0 0 6px',
                    }}>
                      {scene.title}
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 10 }}>
                      <div style={{ height: 1, width: 60, background: 'linear-gradient(90deg,transparent,rgba(45,212,191,0.4))' }} />
                      <span style={{ fontSize: 22 }}>{scene.icon}</span>
                      <div style={{ height: 1, width: 60, background: 'linear-gradient(90deg,rgba(45,212,191,0.4),transparent)' }} />
                    </div>
                  </div>
                )}

                {/* ── TERMINAL ── */}
                <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(30,41,59,0.7)', background: 'rgba(8,14,24,0.97)', boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(45,212,191,0.04), inset 0 1px 0 rgba(255,255,255,0.03)' }}>
                  {/* title bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderBottom: '1px solid rgba(30,41,59,0.6)', background: 'rgba(12,20,35,0.9)' }}>
                    <div style={{ width: 11, height: 11, borderRadius: '50%', background: 'rgba(239,68,68,0.55)' }} />
                    <div style={{ width: 11, height: 11, borderRadius: '50%', background: 'rgba(251,191,36,0.55)' }} />
                    <div style={{ width: 11, height: 11, borderRadius: '50%', background: 'rgba(34,197,94,0.55)' }} />
                    <span style={{ marginLeft: 10, fontFamily: 'monospace', fontSize: 11, color: 'rgba(100,116,139,0.6)', userSelect: 'none' }}>
                      ashwin@journey ~$ story --stop {sceneIndex + 1}/{stations.length}
                    </span>
                  </div>

                  {/* lines area */}
                  <div style={{ padding: '24px 28px', fontFamily: 'monospace', fontSize: 14, lineHeight: 1.75, minHeight: 220, position: 'relative' }}>
                    {/* traveling overlay */}
                    <AnimatePresence>
                      {isTraveling && (
                        <motion.div
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                          style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(8,14,24,0.95)', zIndex: 10, borderRadius: 14 }}
                        >
                          <motion.span
                            animate={{ x: [0, 16, 0, 16, 0] }}
                            transition={{ duration: 0.5, repeat: 1 }}
                            style={{ display: 'block', fontSize: 36, marginBottom: 14 }}
                          >
                            🚂
                          </motion.span>
                          <p style={{ fontFamily: 'monospace', fontSize: 12, letterSpacing: '0.3em', color: 'rgba(45,212,191,0.7)' }}>
                            TRAVELING TO NEXT STOP...
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {displayedLines.map((line, i) => (
                      <p key={i} style={{ margin: 0, height: line === '' ? 14 : 'auto', color: line === '' ? 'transparent' : lineColor(line), fontWeight: line.startsWith('>>') ? 700 : 400, textAlign: line.startsWith('>>') ? 'center' : 'left', letterSpacing: (line.startsWith('>>') || (line.startsWith('[') && line.endsWith(']'))) ? '0.15em' : 'normal' }}>
                        {line}
                      </p>
                    ))}

                    {!isTypingDone && lineIndex < scene.lines.length && (
                      <p style={{ margin: 0, color: lineColor(scene.lines[lineIndex]) }}>
                        {currentLine}
                        <span style={{ display: 'inline-block', width: 8, height: '0.85em', background: '#2dd4bf', marginLeft: 2, verticalAlign: 'middle', animation: 'pulse 1s infinite' }} />
                      </p>
                    )}

                    {isTypingDone && scene.prompt && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0, 1, 0.6, 1] }}
                        transition={{ duration: 1.3 }}
                        style={{ margin: '20px 0 0', fontFamily: 'monospace', fontSize: 12, letterSpacing: '0.25em', color: 'rgba(45,212,191,0.7)' }}
                      >
                        {scene.prompt}
                      </motion.p>
                    )}
                  </div>
                </div>

                <p style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 10, color: 'rgba(51,65,85,0.7)', marginTop: 12, letterSpacing: '0.2em', userSelect: 'none' }}>
                  CLICK · ENTER · SPACE &nbsp;·&nbsp; ESC to skip
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* ── JOURNEY TRACK ── */}
          <div style={{ flexShrink: 0, paddingTop: 20, paddingBottom: 28, borderTop: '1px solid rgba(15,23,42,0.9)', background: 'rgba(5,10,18,0.9)', position: 'relative', zIndex: 10 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.4em', textAlign: 'center', color: 'rgba(51,65,85,0.8)', marginBottom: 20, textTransform: 'uppercase' }}>
              ─── The Journey ───
            </p>
            <JourneyTrack currentIndex={sceneIndex} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
